import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getHero, updateHero, getAbout, updateAbout, getGlobals, updateGlobals, getSectionHeaders, updateSectionHeader, getNavigation, updateNavigation, getFormServices, updateFormServices, getContactMethods, updateContactMethods, getUiStrings, updateUiStrings, getServices, updateService, uploadSiteImage, createService, deleteService, getSettings, updateSetting } from "../../lib/api";
import { Save, Layout, Info, Settings, Briefcase, ChevronRight, Camera, Trash2, Loader2, Plus, ChevronUp, ChevronDown, EyeOff, Type, Image as ImageIcon, BarChart3, MousePointer2, List, Phone, Share2, Upload } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

function mergeDefaults(defaults, existing) {
  const result = { ...defaults };
  for (const key of Object.keys(existing)) {
    if (typeof existing[key] === 'object' && existing[key] !== null && !Array.isArray(existing[key])) {
      result[key] = mergeDefaults(defaults[key] || {}, existing[key]);
    } else {
      result[key] = existing[key];
    }
  }
  return result;
}

export default function AdminSiteContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ hero: false, about: false, services: {} });
  
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [paragraphToDelete, setParagraphToDelete] = useState(null);

  const [hero, setHero] = useState(null);
  const [about, setAbout] = useState(null);
  const [services, setServices] = useState([]);
  const [globals, setGlobals] = useState(null);
  const [sectionHeaders, setSectionHeaders] = useState(null);
  const [navigation, setNavigation] = useState(null);
  const [formServices, setFormServices] = useState(null);
  const [contactMethods, setContactMethods] = useState(null);
  const [uiStrings, setUiStrings] = useState(null);
  const [sections, setSections] = useState(null);
  const [ogSettings, setOgSettings] = useState({ og_title: "", og_description: "", og_image: "", og_url: "" });

  useEffect(() => {
    async function loadData() {
      try {
        const [heroData, aboutData, servicesData, globalsData, headersData, navData, formServicesData, contactMethodsData, uiStringsData, settingsData] = await Promise.all([
          getHero(),
          getAbout(),
          getServices(),
          getGlobals(),
          getSectionHeaders(),
          getNavigation(),
          getFormServices(),
          getContactMethods(),
          getUiStrings(),
          getSettings()
        ]);
        setHero(heroData);
        setAbout(aboutData);
        setServices(servicesData);
        
        // Initialize with defaults if null
        setGlobals(globalsData || {
          logo: "Hub Profissional",
          subtitle: "CONSULTORIA · DESIGN · PATRIMÔNIO",
          cta_label: "Agendar Consultoria",
          footer_description: "Soluções estratégicas para profissionais e empresas que buscam excelência e resultados tangíveis.",
          copyright: "© 2024 Hub Profissional. Todos os direitos reservados.",
          contact_email: "contato@hubprofissional.pt",
          locations: "Lisboa · Porto · Online"
        });
        
        const DEFAULT_HEADERS = {
          services: { eyebrow: "NOSSAS FRENTES", headline: "Soluções Sob Medida", description: "Quatro frentes que se conectam para te levar adiante — patrimônio, estratégia e estética em sintonia." },
          portfolio: { eyebrow: "PORTFÓLIO", headline: "Projetos em Destaque" },
          testimonials: { eyebrow: "DEPOIMENTOS", headline: "Quem Confia no Meu Trabalho" },
          contact: { eyebrow: "CONTATO", headline: "Vamos Iniciar um Projeto?", description: "Escolha a melhor forma de entrar em contato ou preencha o formulário." }
        };
        setSectionHeaders(headersData ? mergeDefaults(DEFAULT_HEADERS, headersData) : DEFAULT_HEADERS);
        
        setNavigation(navData || [
          { label: "Sobre", href: "#sobre" },
          { label: "Serviços", href: "#servicos" },
          { label: "Portfólio", href: "#portfolio" },
          { label: "Depoimentos", href: "#depoimentos" },
          { label: "Contato", href: "#contato" }
        ]);
        
        setFormServices(formServicesData || [
          "Consultoria de Imagem",
          "Design Estratégico",
          "Gestão de Patrimônio",
          "Outros"
        ]);

        setContactMethods(contactMethodsData || [
          "WhatsApp",
          "Ligação Telefónica",
          "E-mail"
        ]);

        const DEFAULT_UI = {
          form: {
            name_label: "Nome Completo",
            name_placeholder: "Como prefere ser chamado?",
            email_label: "E-mail Profissional",
            email_placeholder: "seu@email.com",
            phone_label: "Telefone",
            phone_placeholder: "+351 ...",
            service_label: "Área de Interesse",
            service_placeholder: "Selecione uma opção",
            message_label: "Como posso ajudar?",
            message_placeholder: "Conte-me brevemente sobre seu projeto ou desafio...",
            submit_button: "Enviar Solicitação",
            sending_button: "Enviando...",
            contact_method_label: "Prefere ser contactado via",
            contact_method_placeholder: "Selecione um método"
          },
          feedback: {
            success_title: "Solicitação Recebida!",
            success_message: "Obrigada pelo contato. Em breve entrarei em contato para darmos os próximos passos.",
            error_message: "Ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente ou entre em contato via WhatsApp.",
            received_badge: "Recebido",
            send_new: "Enviar nova mensagem"
          },
          general: {
            learn_more: "Saber mais",
            view_project: "Ver Projeto",
            all_categories: "Todos",
            no_projects: "Nenhum projeto encontrado nesta categoria.",
            scroll: "Scroll",
            hero_img_alt: "Interior sofisticado com luz quente",
            about_img_alt: "Sobre"
          },
          details: {
            whatsapp: "WhatsApp",
            email: "E-mail",
            atendimento: "Atendimento"
          },
          footer: {
            nav_heading: "Navegação",
            contact_heading: "Contato",
            admin_link: "Área restrita"
          }
        };
        setUiStrings(uiStringsData ? mergeDefaults(DEFAULT_UI, uiStringsData) : DEFAULT_UI);

        setSections({
          hero: settingsData?.section_hero_visible !== "false",
          about: settingsData?.section_about_visible !== "false",
          services: settingsData?.section_services_visible !== "false",
          portfolio: settingsData?.section_portfolio_visible !== "false",
          testimonials: settingsData?.section_testimonials_visible !== "false",
          faq: settingsData?.section_faq_visible !== "false",
          contact: settingsData?.section_contact_visible !== "false",
          hero_cta: settingsData?.element_hero_cta_visible !== "false",
          hero_scroll: settingsData?.element_hero_scroll_visible !== "false",
          about_image: settingsData?.element_about_image_visible !== "false",
          about_stats: settingsData?.element_about_stats_visible !== "false",
          services_details: settingsData?.element_services_details_visible !== "false",
          services_carousel: settingsData?.element_services_carousel_visible !== "false",
          faq_form: settingsData?.element_faq_form_visible !== "false",
          faq_items: settingsData?.element_faq_items_visible !== "false",
          contact_info: settingsData?.element_contact_info_visible !== "false",
          contact_form: settingsData?.element_contact_form_visible !== "false",
        });

        setOgSettings({
          og_title: settingsData?.og_title || "Hub Profissional — Emilly Prates",
          og_description: settingsData?.og_description || "Consultoria financeira, Gestão de Patrimônio e Design",
          og_image: settingsData?.og_image || "",
          og_url: settingsData?.og_url || "",
        });
      } catch (err) {
        console.error("Error loading CMS data:", err);
        toast.error("Erro ao carregar dados do site.");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  async function handleImageUpload(e, target, serviceId = null) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (serviceId) {
      setUploading(prev => ({ ...prev, services: { ...prev.services, [serviceId]: true } }));
    } else {
      setUploading(prev => ({ ...prev, [target]: true }));
    }

    try {
      const folder = serviceId ? `services/${serviceId}` : target;
      const url = await uploadSiteImage(file, folder);
      
      if (serviceId) {
        const newS = services.map(s => s.id === serviceId ? { ...s, image_url: url } : s);
        setServices(newS);
        toast.success("Imagem do serviço enviada.");
      } else if (target === "hero") {
        setHero({ ...hero, bg_image: url });
        toast.success("Imagem de fundo del Hero enviada.");
      } else if (target === "about") {
        setAbout({ ...about, image: url });
        toast.success("Imagem da seção Sobre enviada.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Falha ao enviar imagem.");
    } finally {
      if (serviceId) {
        setUploading(prev => ({ ...prev, services: { ...prev.services, [serviceId]: false } }));
      } else {
        setUploading(prev => ({ ...prev, [target]: false }));
      }
    }
  }

  function handleRemoveImage(target, serviceId = null) {
    if (serviceId) {
      const newS = services.map(s => s.id === serviceId ? { ...s, image_url: "" } : s);
      setServices(newS);
    } else if (target === "hero") {
      setHero({ ...hero, bg_image: "" });
    } else if (target === "about") {
      setAbout({ ...about, image: "" });
    }
  }

  async function handleSaveHero() {
    setSaving(true);
    try {
      await updateHero(hero);
      toast.success("Hero atualizado com sucesso.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar Hero.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAbout() {
    setSaving(true);
    try {
      await updateAbout(about);
      toast.success("Seção Sobre atualizada.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar seção Sobre.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveGlobals() {
    setSaving(true);
    try {
      await updateGlobals(globals);
      toast.success("Identidade visual atualizada.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar identidade.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveSectionHeaders() {
    setSaving(true);
    try {
      for (const [section, data] of Object.entries(sectionHeaders)) {
        await updateSectionHeader(section, data);
      }
      toast.success("Títulos das seções atualizados.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar títulos.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveNavigation() {
    setSaving(true);
    try {
      await updateNavigation(navigation);
      toast.success("Menu de navegação atualizado.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar navegação.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveFormServices() {
    setSaving(true);
    try {
      await updateFormServices(formServices);
      toast.success("Opções do formulário atualizadas.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar opções do formulário.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveContactMethods() {
    setSaving(true);
    try {
      await updateContactMethods(contactMethods);
      toast.success("Métodos de contato atualizados.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar métodos de contato.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveUiStrings() {
    setSaving(true);
    try {
      for (const [context, values] of Object.entries(uiStrings)) {
        await updateUiStrings(context, values);
      }
      toast.success("Textos de interface atualizados.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar textos de interface.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveService(id, updates) {
    try {
      await updateService(id, updates);
      toast.success("Serviço atualizado.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao actualizar serviço.");
    }
  }

  async function handleMoveService(index, direction) {
    const newServices = [...services];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newServices.length) return;

    const temp = newServices[index];
    newServices[index] = newServices[targetIndex];
    newServices[targetIndex] = temp;

    setServices(newServices);

    try {
      await Promise.all(newServices.map((s, i) => updateService(s.id, { order_index: i })));
      toast.success("Ordem atualizada.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar nova ordem.");
    }
  }

  async function handleAddService() {
    try {
      const newService = {
        title: "Novo Serviço",
        description: "Descrição do serviço...",
        icon_name: "Briefcase",
        image_url: "",
        bullets: ["Destaque 1", "Destaque 2", "Destaque 3"],
        order_index: services.length,
        published: false
      };
      const created = await createService(newService);
      setServices([...services, created]);
      toast.success("Novo serviço adicionado (Rascunho).");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao adicionar servicio.");
    }
  }

  async function handleDeleteService() {
    if (!serviceToDelete) return;
    try {
      await deleteService(serviceToDelete);
      setServices(services.filter(s => s.id !== serviceToDelete));
      toast.success("Serviço removido.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao remover serviço.");
    } finally {
      setServiceToDelete(null);
    }
  }

  async function handleTogglePublish(id, currentStatus) {
    try {
      const updated = await updateService(id, { published: !currentStatus });
      setServices(services.map(s => s.id === id ? updated : s));
      toast.success(updated.published ? "Serviço publicado." : "Serviço movido para rascunhos.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao alterar status de publicação.");
    }
  }

  if (loading) return <div className="py-20 text-center eyebrow flex flex-col items-center gap-4"><Loader2 className="animate-spin text-champagne" size={32} /> Carregando editor...</div>;

  return (
    <div className="max-w-6xl mx-auto pb-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 border-b border-fendi/20 pb-8">
        <div>
          <h2 className="font-serif text-4xl text-softblack mb-2 flex items-center gap-3">
             Gestão do Site
          </h2>
          <p className="text-warmgray text-sm flex items-center gap-2">
            <MousePointer2 size={14} /> Personalize a identidade visual e o conteúdo da página inicial.
          </p>
        </div>
        <button onClick={() => window.open('/', '_blank')} className="btn-ghost !py-2 flex items-center gap-2">
           Ver Site Público <ChevronRight size={14} />
        </button>
      </div>

      <Tabs defaultValue="hero" className="w-full">
        {/* Mobile tabs */}
        <div className="md:hidden">
          <TabsList className="w-full border-b border-fendi p-0 h-auto justify-around flex-nowrap rounded-none bg-fendi/5">
            {[
              { value: "hero", icon: ImageIcon, label: "Hero" },
              { value: "about", icon: Info, label: "Sobre" },
              { value: "services", icon: Briefcase, label: "Serviços" },
              { value: "identity", icon: Layout, label: "Identidade" },
              { value: "headers", icon: Type, label: "Títulos" },
              { value: "lists", icon: List, label: "Listas" },
              { value: "sections", icon: EyeOff, label: "Seções" },
              { value: "sharing", icon: Share2, label: "Compartilhar" },
              { value: "interface", icon: MousePointer2, label: "Interface" },
            ].map(({ value, icon: Icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="shrink-0 px-3 py-3 data-[state=active]:bg-white data-[state=active]:text-champagne data-[state=active]:border-b-2 data-[state=active]:border-b-champagne transition-all rounded-none border-b-2 border-b-transparent"
              >
                <Icon size={18} />
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex gap-0 bg-white border border-fendi">
          {/* Desktop sidebar */}
          <TabsList className="hidden md:flex flex-col w-52 shrink-0 border-r border-fendi p-0 h-auto justify-start rounded-none bg-fendi/5">
            {[
              { value: "hero", icon: ImageIcon, label: "Hero" },
              { value: "about", icon: Info, label: "Sobre" },
              { value: "services", icon: Briefcase, label: "Serviços" },
              { value: "identity", icon: Layout, label: "Identidade" },
              { value: "headers", icon: Type, label: "Títulos" },
              { value: "lists", icon: List, label: "Listas" },
              { value: "sections", icon: EyeOff, label: "Seções" },
              { value: "sharing", icon: Share2, label: "Compartilhar" },
              { value: "interface", icon: MousePointer2, label: "Interface" },
            ].map(({ value, icon: Icon, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="w-full justify-start gap-3 px-5 py-3.5 text-[11px] tracking-[0.15em] uppercase data-[state=active]:bg-white data-[state=active]:text-champagne data-[state=active]:border-l-2 data-[state=active]:border-l-champagne transition-all rounded-none border-l-2 border-l-transparent"
              >
                <Icon size={15} /> {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Content area */}
          <div className="flex-1 p-4 md:p-10 overflow-y-auto">

        {/* INTERFACE SECTION */}
        <TabsContent value="interface" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="space-y-8 max-w-4xl">
            {/* Form Strings */}
            <div className="bg-white border border-fendi p-8 shadow-sm">
              <p className="eyebrow mb-8 flex items-center gap-2 text-champagne"><Type size={16} /> Formulário de Contato</p>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Label: Nome</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.name_label || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, name_label: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Placeholder: Nome</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.name_placeholder || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, name_placeholder: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Label: E-mail</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.email_label || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, email_label: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Placeholder: E-mail</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.email_placeholder || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, email_placeholder: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Label: Telefone</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.phone_label || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, phone_label: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Placeholder: Telefone</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.phone_placeholder || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, phone_placeholder: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Label: Serviço</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.service_label || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, service_label: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Placeholder: Serviço</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.service_placeholder || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, service_placeholder: e.target.value}})} />
                </label>
                <label className="block md:col-span-2">
                  <span className="eyebrow text-[10px] text-warmgray">Label: Mensagem</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.message_label || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, message_label: e.target.value}})} />
                </label>
                <label className="block md:col-span-2">
                  <span className="eyebrow text-[10px] text-warmgray">Placeholder: Mensagem</span>
                  <textarea className="editorial-field mt-2 !text-xs" rows={2} value={uiStrings?.form?.message_placeholder || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, message_placeholder: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Label: Método de Contato</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.contact_method_label || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, contact_method_label: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Placeholder: Método de Contato</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.contact_method_placeholder || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, contact_method_placeholder: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Botão: Enviar</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.submit_button || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, submit_button: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Botúo: Enviando...</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.form?.sending_button || ""} 
                    onChange={e => setUiStrings({...uiStrings, form: {...uiStrings.form, sending_button: e.target.value}})} />
                </label>
              </div>
              <div className="flex justify-end mt-6 pt-4 border-t border-fendi/10">
                <button onClick={handleSaveUiStrings} disabled={saving} className="btn-primary !px-8 !py-2 !text-[10px]">
                  <Save size={12} className="mr-2" /> {saving ? "Salvando..." : "Salvar Formulário"}
                </button>
              </div>
            </div>

            {/* Feedback Strings */}
            <div className="bg-white border border-fendi p-8 shadow-sm">
              <p className="eyebrow mb-8 flex items-center gap-2 text-champagne"><Info size={16} /> Mensagens de Feedback</p>
              <div className="space-y-6">
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Título de Sucesso</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.feedback?.success_title || ""} 
                    onChange={e => setUiStrings({...uiStrings, feedback: {...uiStrings.feedback, success_title: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Mensagem de Sucesso</span>
                  <textarea className="editorial-field mt-2 !text-xs" rows={2} value={uiStrings?.feedback?.success_message || ""} 
                    onChange={e => setUiStrings({...uiStrings, feedback: {...uiStrings.feedback, success_message: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Mensagem de Erro</span>
                  <textarea className="editorial-field mt-2 !text-xs" rows={2} value={uiStrings?.feedback?.error_message || ""} 
                    onChange={e => setUiStrings({...uiStrings, feedback: {...uiStrings.feedback, error_message: e.target.value}})} />
                </label>
                <div className="grid md:grid-cols-2 gap-6">
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Badge: Recebido</span>
                    <input className="editorial-field mt-2 !text-xs" value={uiStrings?.feedback?.received_badge || ""} 
                      onChange={e => setUiStrings({...uiStrings, feedback: {...uiStrings.feedback, received_badge: e.target.value}})} />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Botão: Enviar nova mensagem</span>
                    <input className="editorial-field mt-2 !text-xs" value={uiStrings?.feedback?.send_new || ""} 
                      onChange={e => setUiStrings({...uiStrings, feedback: {...uiStrings.feedback, send_new: e.target.value}})} />
                  </label>
                </div>
              </div>
              <div className="flex justify-end mt-6 pt-4 border-t border-fendi/10">
                <button onClick={handleSaveUiStrings} disabled={saving} className="btn-primary !px-8 !py-2 !text-[10px]">
                  <Save size={12} className="mr-2" /> {saving ? "Salvando..." : "Salvar Feedback"}
                </button>
              </div>
            </div>

            {/* General UI Strings */}
            <div className="bg-white border border-fendi p-8 shadow-sm">
              <p className="eyebrow mb-8 flex items-center gap-2 text-champagne"><MousePointer2 size={16} /> UI Geral</p>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Botão: Saber Mais</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.general?.learn_more || ""} 
                    onChange={e => setUiStrings({...uiStrings, general: {...uiStrings.general, learn_more: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Botão: Ver Projeto</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.general?.view_project || ""} 
                    onChange={e => setUiStrings({...uiStrings, general: {...uiStrings.general, view_project: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Filtro: Todas Categorias</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.general?.all_categories || ""} 
                    onChange={e => setUiStrings({...uiStrings, general: {...uiStrings.general, all_categories: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Aviso: Sem Projetos</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.general?.no_projects || ""} 
                    onChange={e => setUiStrings({...uiStrings, general: {...uiStrings.general, no_projects: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Texto: Scroll (Hero)</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.general?.scroll || ""} 
                    onChange={e => setUiStrings({...uiStrings, general: {...uiStrings.general, scroll: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Alt: Imagem do Hero</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.general?.hero_img_alt || ""} 
                    onChange={e => setUiStrings({...uiStrings, general: {...uiStrings.general, hero_img_alt: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Alt: Imagem do Sobre</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.general?.about_img_alt || ""} 
                    onChange={e => setUiStrings({...uiStrings, general: {...uiStrings.general, about_img_alt: e.target.value}})} />
                </label>
              </div>
              <div className="flex justify-end mt-6 pt-4 border-t border-fendi/10">
                <button onClick={handleSaveUiStrings} disabled={saving} className="btn-primary !px-8 !py-2 !text-[10px]">
                  <Save size={12} className="mr-2" /> {saving ? "Salvando..." : "Salvar Geral"}
                </button>
              </div>
            </div>

            {/* Detail Labels */}
            <div className="bg-white border border-fendi p-8 shadow-sm">
              <p className="eyebrow mb-8 flex items-center gap-2 text-champagne"><Info size={16} /> Labels de Detalhes (Contato)</p>
              <div className="grid md:grid-cols-3 gap-x-8 gap-y-6">
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Label: WhatsApp</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.details?.whatsapp || ""} 
                    onChange={e => setUiStrings({...uiStrings, details: {...uiStrings.details, whatsapp: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Label: E-mail</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.details?.email || ""} 
                    onChange={e => setUiStrings({...uiStrings, details: {...uiStrings.details, email: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Label: Atendimento</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.details?.atendimento || ""} 
                    onChange={e => setUiStrings({...uiStrings, details: {...uiStrings.details, atendimento: e.target.value}})} />
                </label>
              </div>
              <div className="flex justify-end mt-6 pt-4 border-t border-fendi/10">
                <button onClick={handleSaveUiStrings} disabled={saving} className="btn-primary !px-8 !py-2 !text-[10px]">
                  <Save size={12} className="mr-2" /> {saving ? "Salvando..." : "Salvar Detalhes"}
                </button>
              </div>
            </div>

            {/* Footer Strings */}
            <div className="bg-white border border-fendi p-8 shadow-sm">
              <p className="eyebrow mb-8 flex items-center gap-2 text-champagne"><Layout size={16} /> Textos do Rodapé</p>
              <div className="grid md:grid-cols-3 gap-x-8 gap-y-6">
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Título: Navegação</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.footer?.nav_heading || ""} 
                    onChange={e => setUiStrings({...uiStrings, footer: {...uiStrings.footer, nav_heading: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Título: Contato</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.footer?.contact_heading || ""} 
                    onChange={e => setUiStrings({...uiStrings, footer: {...uiStrings.footer, contact_heading: e.target.value}})} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Link: Área restrita</span>
                  <input className="editorial-field mt-2 !text-xs" value={uiStrings?.footer?.admin_link || ""} 
                    onChange={e => setUiStrings({...uiStrings, footer: {...uiStrings.footer, admin_link: e.target.value}})} />
                </label>
              </div>
              <div className="flex justify-end mt-6 pt-4 border-t border-fendi/10">
                <button onClick={handleSaveUiStrings} disabled={saving} className="btn-primary !px-8 !py-2 !text-[10px]">
                  <Save size={12} className="mr-2" /> {saving ? "Salvando..." : "Salvar Rodapé"}
                </button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* HERO SECTION REDESIGN */}
        <TabsContent value="hero" className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white border border-fendi p-6 sticky top-24">
                <p className="eyebrow mb-6 flex items-center gap-2 text-champagne"><ImageIcon size={16} /> Visual da Capa</p>
                <div className="relative group w-full aspect-[16/10] bg-fendi/5 overflow-hidden border border-fendi/20 shadow-inner">
                  {hero?.bg_image ? (
                    <img src={hero.bg_image} alt="Hero Preview" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-warmgray/40"><ImageIcon size={48} strokeWidth={1} /></div>
                  )}
                  <label className="absolute inset-0 bg-softblack/0 group-hover:bg-softblack/40 transition-all cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white">
                    {hero?.bg_image && (
                      <button 
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveImage("hero"); }}
                        className="absolute top-4 right-4 bg-white/10 hover:bg-destructive p-2 rounded-full transition-all"
                        title="Remover Imagem"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    {uploading.hero ? <Loader2 className="animate-spin" /> : <><Camera size={24} className="mb-2" /><span className="text-[10px] uppercase tracking-widest font-medium">Trocar Imagem</span></>}
                    <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, "hero")} disabled={uploading.hero} />
                  </label>
                </div>
                <div className="mt-6 space-y-4">
                   <div className="bg-offwhite p-4 border-l-2 border-champagne">
                      <p className="text-[10px] text-warmgray leading-relaxed italic">
                        Esta é a primeira imagem que os clientes verão. Use uma foto de alta qualidade (2400px+) com tons que combinem com a marca Hub Profissional.
                      </p>
                   </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white border border-fendi p-8 shadow-sm">
                <p className="eyebrow mb-8 flex items-center gap-2 text-champagne"><Type size={16} /> Conteúdo Editorial</p>
                <div className="space-y-8">
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Slogan Superior (Eyebrow)</span>
                    <input className="editorial-field mt-3 !text-xs tracking-[0.2em]" value={hero?.eyebrow || ""} 
                      onChange={e => setHero({...hero, eyebrow: e.target.value})} placeholder="Ex: CONSULTORIA · DESIGN · PATRIMÔNIO" />
                  </label>
                  
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Título de Impacto (Headline)</span>
                    <textarea className="editorial-field mt-3 !text-2xl font-serif !leading-snug" rows={3} value={hero?.headline || ""} 
                      onChange={e => setHero({...hero, headline: e.target.value})} placeholder="O título principal do seu site..." />
                  </label>

                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Texto de Apoio (Subheadline)</span>
                    <textarea className="editorial-field mt-3 text-base font-light !leading-relaxed text-warmgray" rows={4} value={hero?.subheadline || ""} 
                      onChange={e => setHero({...hero, subheadline: e.target.value})} placeholder="Uma breve descrição que complementa o título..." />
                  </label>
                </div>

                <div className="mt-12 pt-8 border-t border-fendi/10 space-y-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    <label className="block">
                      <span className="eyebrow text-[10px] text-warmgray">Botão Principal (WhatsApp)</span>
                      <input className="editorial-field mt-3 !py-3 !text-[11px]" value={hero?.cta_primary || ""} 
                        onChange={e => setHero({...hero, cta_primary: e.target.value})} />
                    </label>
                    <label className="block">
                      <span className="eyebrow text-[10px] text-warmgray">Botão Secundário (Serviços)</span>
                      <input className="editorial-field mt-3 !py-3 !text-[11px]" value={hero?.cta_secondary || ""} 
                        onChange={e => setHero({...hero, cta_secondary: e.target.value})} />
                    </label>
                  </div>
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Texto alternativo da imagem (acessibilidade/SEO — não aparece na página)</span>
                    <input className="editorial-field mt-3 !text-xs" value={hero?.img_alt || ""} 
                      onChange={e => setHero({...hero, img_alt: e.target.value})} placeholder="Descreva a imagem para leitores de tela..." />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Mensagem padrão do WhatsApp</span>
                    <textarea className="editorial-field mt-3 !text-xs" rows={2} value={hero?.whatsapp_message || ""} 
                      onChange={e => setHero({...hero, whatsapp_message: e.target.value})} />
                  </label>
                </div>

                <div className="mt-12 flex justify-end">
                  <button onClick={handleSaveHero} disabled={saving} className="btn-primary !px-10 !py-4 shadow-lg shadow-champagne/10">
                    <Save size={16} className="mr-3" /> {saving ? "Salvando Alterações..." : "Publicar no Hero"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ABOUT SECTION */}
        <TabsContent value="about" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="space-y-12">
            {/* Top: Image + Basic settings */}
            <div className="grid xl:grid-cols-12 gap-10">
              {/* Image panel */}
              <div className="xl:col-span-4">
                <div className="sticky top-24">
                  <p className="eyebrow mb-4 text-champagne">Retrato Editorial</p>
                  <div className="relative group aspect-[3/4] bg-fendi/5 overflow-hidden shadow-sm">
                    {about?.image ? (
                      <img src={about.image} alt="About" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-warmgray/30 gap-3">
                        <Camera size={40} strokeWidth={1} />
                        <span className="text-[10px] uppercase tracking-widest">Sem imagem</span>
                      </div>
                    )}
                    <label className="absolute inset-0 bg-softblack/0 group-hover:bg-softblack/50 transition-all cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white">
                      {about?.image && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveImage("about"); }}
                          className="absolute top-3 right-3 bg-white/10 hover:bg-destructive p-1.5 rounded-full transition-all"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {uploading.about ? <Loader2 className="animate-spin" /> : <><Camera size={22} className="mb-1.5" /><span className="text-[9px] uppercase tracking-widest">Trocar</span></>}
                      <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, "about")} disabled={uploading.about} />
                    </label>
                  </div>
                  <label className="block mt-4">
                    <span className="eyebrow text-[10px] text-warmgray">Alt da imagem (SEO — não aparece na página)</span>
                    <input className="editorial-field mt-2 !text-xs" value={about?.img_alt || ""}
                      onChange={e => setAbout({...about, img_alt: e.target.value})} placeholder="Descreva a imagem..." />
                  </label>
                </div>
              </div>

              {/* Right column */}
              <div className="xl:col-span-8 space-y-10">
                {/* Eyebrow + Headline */}
                <div>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <label className="block">
                      <span className="eyebrow text-[10px] text-warmgray">Eyebrow</span>
                      <input className="editorial-field mt-2 !text-[10px] tracking-[0.2em]" value={about?.eyebrow || ""}
                        onChange={e => setAbout({...about, eyebrow: e.target.value})} />
                    </label>
                    <label className="block">
                      <span className="eyebrow text-[10px] text-warmgray">Atua desde</span>
                      <input className="editorial-field mt-2 !text-xs" value={about?.since || ""} placeholder="Ex: 2014"
                        onChange={e => setAbout({...about, since: e.target.value})} />
                    </label>
                  </div>
                  <label className="block mb-6">
                    <span className="eyebrow text-[10px] text-warmgray">WhatsApp message (opcional)</span>
                    <input className="editorial-field mt-2 !text-xs" value={about?.whatsapp_message || ""} placeholder="Mensagem padrão..."
                      onChange={e => setAbout({...about, whatsapp_message: e.target.value})} />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Título</span>
                    <input className="editorial-field mt-2 !text-2xl font-serif" value={about?.headline || ""}
                      onChange={e => setAbout({...about, headline: e.target.value})} />
                  </label>
                </div>

                {/* Divider */}
                <div className="h-px bg-fendi/20" />

                {/* Paragraphs */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <p className="eyebrow text-champagne">Parágrafos</p>
                    <button
                      type="button"
                      onClick={() => {
                        const current = about.paragraphs || [about.description_1 || "", about.description_2 || ""].filter(Boolean);
                        setAbout({ ...about, paragraphs: [...current, ""] });
                      }}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-champagne hover:text-softblack transition-colors"
                    >
                      <Plus size={12} /> Adicionar
                    </button>
                  </div>
                  <div className="space-y-6">
                    {(about.paragraphs || [about.description_1 || "", about.description_2 || ""].filter(Boolean)).map((p, idx, arr) => (
                      <div key={idx} className="flex gap-4 items-start group">
                        <span className="text-[11px] font-serif text-champagne/60 mt-1.5 shrink-0 tabular-nums w-5 text-right">{String(idx + 1).padStart(2, '0')}</span>
                        <textarea
                          className="editorial-field text-sm font-light !leading-relaxed flex-1"
                          rows={4}
                          value={p}
                          onChange={e => {
                            const paragraphs = [...arr];
                            paragraphs[idx] = e.target.value;
                            setAbout({ ...about, paragraphs });
                          }}
                          placeholder={`Parágrafo ${idx + 1}...`}
                        />
                        <button
                          type="button"
                          onClick={() => setParagraphToDelete(idx)}
                          className="mt-1 p-1.5 text-fendi/40 hover:text-destructive transition-colors shrink-0 opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-fendi/20" />

                {/* Stats */}
                <div>
                  <p className="eyebrow mb-6 text-champagne">Indicadores</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {about?.stats?.map((stat, idx) => (
                      <div key={idx} className="group/stat">
                        <div className="flex items-end gap-2 mb-2">
                          <input className="bg-transparent border-b border-fendi/20 focus:border-champagne outline-none !text-xs w-8 text-center text-warmgray pb-1 shrink-0"
                            placeholder="+"
                            value={stat.prefix || ""}
                            onChange={e => {
                              const newStats = [...about.stats];
                              newStats[idx].prefix = e.target.value;
                              setAbout({...about, stats: newStats});
                            }} />
                          <input className="bg-transparent border-b border-fendi/20 focus:border-champagne outline-none !text-3xl font-serif text-softblack pb-1 flex-1 min-w-0 transition-colors"
                            value={stat.n}
                            onChange={e => {
                              const newStats = [...about.stats];
                              newStats[idx].n = e.target.value;
                              setAbout({...about, stats: newStats});
                            }} />
                        </div>
                        <input className="bg-transparent border-none focus:ring-0 outline-none text-[10px] uppercase tracking-[0.2em] text-warmgray w-full truncate"
                          value={stat.label}
                          onChange={e => {
                            const newStats = [...about.stats];
                            newStats[idx].label = e.target.value;
                            setAbout({...about, stats: newStats});
                          }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Save */}
            <div className="flex justify-end pt-6 border-t border-fendi/10">
              <button onClick={handleSaveAbout} disabled={saving} className="btn-primary !px-10 !py-4 shadow-lg shadow-champagne/10">
                <Save size={16} className="mr-3" /> {saving ? "Salvando..." : "Publicar Alterações"}
              </button>
            </div>
          </div>
        </TabsContent>

           {/* IDENTITY SECTION */}
           <TabsContent value="identity" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
           <div className="bg-white border border-fendi p-8 max-w-4xl shadow-sm">
             <p className="eyebrow mb-8 flex items-center gap-2 text-champagne"><Layout size={16} /> Identidade Visual & Branding</p>
             <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
               <div className="space-y-8">
                 <label className="block">
                   <span className="eyebrow text-[10px] text-warmgray">Logo (Texto)</span>
                   <input className="editorial-field mt-3 !font-serif !text-xl" value={globals?.logo || ""} 
                     onChange={e => setGlobals({...globals, logo: e.target.value})} />
                 </label>
                 <label className="block">
                   <span className="eyebrow text-[10px] text-warmgray">Slogan / Subtítulo</span>
                   <input className="editorial-field mt-3 !text-[10px] tracking-[0.2em]" value={globals?.subtitle || ""} 
                     onChange={e => setGlobals({...globals, subtitle: e.target.value})} />
                 </label>
                 <label className="block">
                   <span className="eyebrow text-[10px] text-warmgray">Texto do Botão CTA Global</span>
                   <input className="editorial-field mt-3" value={globals?.cta_label || ""} 
                     onChange={e => setGlobals({...globals, cta_label: e.target.value})} />
                 </label>
               </div>
               <div className="space-y-8">
                 <label className="block">
                   <span className="eyebrow text-[10px] text-warmgray">E-mail de Contato Principal</span>
                   <input className="editorial-field mt-3" value={globals?.contact_email || ""} 
                     onChange={e => setGlobals({...globals, contact_email: e.target.value})} />
                 </label>
                 <label className="block">
                   <span className="eyebrow text-[10px] text-warmgray">Localizações (Separe por ponto médio)</span>
                   <input className="editorial-field mt-3" value={globals?.locations || ""} 
                     onChange={e => setGlobals({...globals, locations: e.target.value})} />
                 </label>
                 <label className="block">
                   <span className="eyebrow text-[10px] text-warmgray">Texto de Copyright (Rodapé)</span>
                   <input className="editorial-field mt-3" value={globals?.copyright || ""} 
                     onChange={e => setGlobals({...globals, copyright: e.target.value})} />
                 </label>
               </div>
               <div className="md:col-span-2">
                 <label className="block">
                   <span className="eyebrow text-[10px] text-warmgray">Descrição Curta (Rodapé)</span>
                   <textarea className="editorial-field mt-3 text-sm font-light !leading-relaxed" rows={3} value={globals?.footer_description || ""} 
                     onChange={e => setGlobals({...globals, footer_description: e.target.value})} />
                 </label>
               </div>
             </div>
              <div className="md:col-span-2 mt-8 pt-8 border-t border-fendi/10">
                <p className="eyebrow mb-6 flex items-center gap-2 text-champagne"><Settings size={16} /> Contactos</p>
                <div className="grid md:grid-cols-2 gap-8">
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">WhatsApp (com código do país)</span>
                    <input className="editorial-field mt-3" value={globals?.whatsapp_number || ""} 
                      onChange={e => setGlobals({...globals, whatsapp_number: e.target.value})} placeholder="+351..." />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">E-mail de Contato Público</span>
                    <input className="editorial-field mt-3" value={globals?.contact_email || ""} 
                      onChange={e => setGlobals({...globals, contact_email: e.target.value})} placeholder="contato@..." />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">E-mail de Notificação (Leads)</span>
                    <input className="editorial-field mt-3" value={globals?.notification_email || ""} 
                      onChange={e => setGlobals({...globals, notification_email: e.target.value})} placeholder="notificacoes@..." />
                  </label>
                </div>
              </div>
              <div className="mt-12 flex justify-end">
                <button onClick={handleSaveGlobals} disabled={saving} className="btn-primary !px-10">
                  <Save size={14} className="mr-3" /> {saving ? "Salvando..." : "Salvar Identidade"}
                </button>
              </div>
            </div>
            </TabsContent>

           {/* SECTION HEADERS */}
           <TabsContent value="headers" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
             <div className="bg-white border border-fendi p-8 max-w-4xl shadow-sm">
               <p className="eyebrow mb-8 flex items-center gap-2 text-champagne"><Type size={16} /> Títulos e Chamadas de Seção</p>
               <div className="grid md:grid-cols-2 gap-12">
                 {/* Services Headers */}
                 <div className="space-y-6">
                   <p className="font-serif text-xl border-b border-fendi/20 pb-2">Seção de Serviços</p>
                   <label className="block">
                     <span className="eyebrow text-[10px] text-warmgray">Eyebrow (Slogan)</span>
                     <input className="editorial-field mt-2 !text-[10px] tracking-[0.2em]" value={sectionHeaders?.services?.eyebrow || ""} 
                       onChange={e => setSectionHeaders({...sectionHeaders, services: {...sectionHeaders.services, eyebrow: e.target.value}})} />
                   </label>
                    <label className="block">
                      <span className="eyebrow text-[10px] text-warmgray">Headline (Título)</span>
                      <input className="editorial-field mt-2" value={sectionHeaders?.services?.headline || ""} 
                        onChange={e => setSectionHeaders({...sectionHeaders, services: {...sectionHeaders.services, headline: e.target.value}})} />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="eyebrow text-[10px] text-warmgray">Descrição da Seção</span>
                      <textarea className="editorial-field mt-2 text-sm" rows={2} value={sectionHeaders?.services?.description || ""} 
                        onChange={e => setSectionHeaders({...sectionHeaders, services: {...sectionHeaders.services, description: e.target.value}})} />
                    </label>
                  </div>

                  {/* Portfolio Headers */}
                 <div className="space-y-6">
                   <p className="font-serif text-xl border-b border-fendi/20 pb-2">Seção de Portfólio</p>
                   <label className="block">
                     <span className="eyebrow text-[10px] text-warmgray">Eyebrow (Slogan)</span>
                     <input className="editorial-field mt-2 !text-[10px] tracking-[0.2em]" value={sectionHeaders?.portfolio?.eyebrow || ""} 
                       onChange={e => setSectionHeaders({...sectionHeaders, portfolio: {...sectionHeaders.portfolio, eyebrow: e.target.value}})} />
                   </label>
                   <label className="block">
                     <span className="eyebrow text-[10px] text-warmgray">Headline (Título)</span>
                     <input className="editorial-field mt-2" value={sectionHeaders?.portfolio?.headline || ""} 
                       onChange={e => setSectionHeaders({...sectionHeaders, portfolio: {...sectionHeaders.portfolio, headline: e.target.value}})} />
                   </label>
                 </div>

                 {/* Testimonials Headers */}
                 <div className="space-y-6">
                   <p className="font-serif text-xl border-b border-fendi/20 pb-2">Seção de Depoimentos</p>
                   <label className="block">
                     <span className="eyebrow text-[10px] text-warmgray">Eyebrow (Slogan)</span>
                     <input className="editorial-field mt-2 !text-[10px] tracking-[0.2em]" value={sectionHeaders?.testimonials?.eyebrow || ""} 
                       onChange={e => setSectionHeaders({...sectionHeaders, testimonials: {...sectionHeaders.testimonials, eyebrow: e.target.value}})} />
                   </label>
                   <label className="block">
                     <span className="eyebrow text-[10px] text-warmgray">Headline (Título)</span>
                     <input className="editorial-field mt-2" value={sectionHeaders?.testimonials?.headline || ""} 
                       onChange={e => setSectionHeaders({...sectionHeaders, testimonials: {...sectionHeaders.testimonials, headline: e.target.value}})} />
                   </label>
                 </div>

                 {/* Contact Headers */}
                 <div className="space-y-6">
                   <p className="font-serif text-xl border-b border-fendi/20 pb-2">Seção de Contato</p>
                   <label className="block">
                     <span className="eyebrow text-[10px] text-warmgray">Eyebrow (Slogan)</span>
                     <input className="editorial-field mt-2 !text-[10px] tracking-[0.2em]" value={sectionHeaders?.contact?.eyebrow || ""} 
                       onChange={e => setSectionHeaders({...sectionHeaders, contact: {...sectionHeaders.contact, eyebrow: e.target.value}})} />
                   </label>
                   <label className="block">
                     <span className="eyebrow text-[10px] text-warmgray">Headline (Título)</span>
                     <input className="editorial-field mt-2" value={sectionHeaders?.contact?.headline || ""} 
                       onChange={e => setSectionHeaders({...sectionHeaders, contact: {...sectionHeaders.contact, headline: e.target.value}})} />
                   </label>
                 </div>
                 
                 <div className="md:col-span-2">
                   <label className="block">
                     <span className="eyebrow text-[10px] text-warmgray">Descrição da Seção de Contato</span>
                     <textarea className="editorial-field mt-2 text-sm" rows={2} value={sectionHeaders?.contact?.description || ""} 
                       onChange={e => setSectionHeaders({...sectionHeaders, contact: {...sectionHeaders.contact, description: e.target.value}})} />
                   </label>
                 </div>
               </div>
               <div className="mt-12 flex justify-end">
                 <button onClick={handleSaveSectionHeaders} disabled={saving} className="btn-primary !px-10">
                   <Save size={14} className="mr-3" /> {saving ? "Salvando..." : "Salvar Títulos"}
                 </button>
               </div>
             </div>
             </TabsContent>

             {/* LISTS SECTION */}
             <TabsContent value="lists" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
             <div className="grid lg:grid-cols-2 gap-8">
               {/* Navigation Manager */}
               <div className="bg-white border border-fendi p-8 shadow-sm">
                 <div className="flex justify-between items-center mb-8 pb-4 border-b border-fendi/10">
                   <p className="eyebrow flex items-center gap-2 text-champagne"><MousePointer2 size={16} /> Links de Navegação</p>
                   <button 
                     onClick={() => setNavigation([...navigation, { label: "Novo Link", href: "#" }])}
                     className="text-[10px] uppercase tracking-widest hover:text-champagne transition-colors flex items-center gap-1"
                   >
                     <Plus size={12} /> Adicionar Item
                   </button>
                 </div>
                 <div className="space-y-4">
                   {navigation?.map((item, idx) => (
                     <div key={idx} className="flex items-end gap-3 bg-fendi/5 p-4 border border-fendi/10 group">
                       <div className="flex-1 grid grid-cols-2 gap-4">
                         <label className="block">
                           <span className="text-[9px] uppercase tracking-wider text-warmgray">Label</span>
                           <input className="editorial-field mt-1 !py-1.5 !text-xs" value={item.label} 
                             onChange={e => {
                               const newNav = [...navigation];
                               newNav[idx].label = e.target.value;
                               setNavigation(newNav);
                             }} />
                         </label>
                         <label className="block">
                           <span className="text-[9px] uppercase tracking-wider text-warmgray">Href / Link</span>
                           <input className="editorial-field mt-1 !py-1.5 !text-xs" value={item.href} 
                             onChange={e => {
                               const newNav = [...navigation];
                               newNav[idx].href = e.target.value;
                               setNavigation(newNav);
                             }} />
                         </label>
                       </div>
                       <button 
                         onClick={() => setNavigation(navigation.filter((_, i) => i !== idx))}
                         className="p-2 text-warmgray/40 hover:text-destructive transition-colors"
                       >
                         <Trash2 size={14} />
                       </button>
                     </div>
                   ))}
                 </div>
                 <div className="mt-10 flex justify-end">
                   <button onClick={handleSaveNavigation} disabled={saving} className="btn-primary !py-3 !px-8">
                     <Save size={14} className="mr-3" /> Salvar Menu
                   </button>
                 </div>
               </div>

               {/* Form Services Manager */}
               <div className="bg-white border border-fendi p-8 shadow-sm">
                 <div className="flex justify-between items-center mb-8 pb-4 border-b border-fendi/10">
                   <p className="eyebrow flex items-center gap-2 text-champagne"><Briefcase size={16} /> Opções do Formulário</p>
                   <button 
                     onClick={() => setFormServices([...formServices, "Novo Serviço"])}
                     className="text-[10px] uppercase tracking-widest hover:text-champagne transition-colors flex items-center gap-1"
                   >
                     <Plus size={12} /> Adicionar Opção
                   </button>
                 </div>
                 <div className="space-y-4">
                   {formServices?.map((service, idx) => (
                     <div key={idx} className="flex items-center gap-3 bg-fendi/5 p-4 border border-fendi/10 group">
                       <input className="editorial-field !py-1.5 !text-xs flex-1" value={service} 
                         onChange={e => {
                           const newServices = [...formServices];
                           newServices[idx] = e.target.value;
                           setFormServices(newServices);
                         }} />
                       <button 
                         onClick={() => setFormServices(formServices.filter((_, i) => i !== idx))}
                         className="p-2 text-warmgray/40 hover:text-destructive transition-colors"
                       >
                         <Trash2 size={14} />
                       </button>
                     </div>
                   ))}
                </div>
                <div className="mt-10 flex justify-end">
                  <button onClick={handleSaveFormServices} disabled={saving} className="btn-primary !py-3 !px-8">
                    <Save size={14} className="mr-3" /> Salvar Opções
                  </button>
                </div>
              </div>

              {/* Contact Methods Manager */}
              <div className="bg-white border border-fendi p-8 shadow-sm">
                <div className="flex justify-between items-center mb-8 pb-4 border-b border-fendi/10">
                  <p className="eyebrow flex items-center gap-2 text-champagne"><Phone size={16} /> Métodos de Contato</p>
                  <button 
                    onClick={() => setContactMethods([...contactMethods, "Novo Método"])}
                    className="text-[10px] uppercase tracking-widest hover:text-champagne transition-colors flex items-center gap-1"
                  >
                    <Plus size={12} /> Adicionar Método
                  </button>
                </div>
                <div className="space-y-4">
                  {contactMethods?.map((method, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-fendi/5 p-4 border border-fendi/10 group">
                      <input className="editorial-field !py-1.5 !text-xs flex-1" value={method} 
                        onChange={e => {
                          const newMethods = [...contactMethods];
                          newMethods[idx] = e.target.value;
                          setContactMethods(newMethods);
                        }} />
                      <button 
                        onClick={() => setContactMethods(contactMethods.filter((_, i) => i !== idx))}
                        className="p-2 text-warmgray/40 hover:text-destructive transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-10 flex justify-end">
                  <button onClick={handleSaveContactMethods} disabled={saving} className="btn-primary !py-3 !px-8">
                    <Save size={14} className="mr-3" /> Salvar Métodos
                  </button>
                </div>
              </div>
            </div>
             </TabsContent>

              {/* SECTIONS SECTION */}
              <TabsContent value="sections" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="space-y-6 max-w-4xl">
                  <div className="bg-white border border-fendi p-8 shadow-sm">
                    <p className="eyebrow mb-2 text-champagne">Visibilidade das Seções</p>
                    <p className="text-warmgray text-xs mb-8">Ative ou desative cada seção da página principal. Se desativada, não aparece no site nem no menu.</p>
                    <div className="space-y-4">
                      {[
                        { key: "hero", label: "Hero", desc: "Banner principal com chamada para ação", elements: [{ key: "hero_cta", label: "Botões CTA" }, { key: "hero_scroll", label: "Indicador de scroll" }] },
                        { key: "services", label: "Serviços", desc: "Frentes de atuação", elements: [{ key: "services_carousel", label: "Carousel / Tarjetas" }, { key: "services_details", label: "Detalhes por serviço" }] },
                        { key: "about", label: "Sobre", desc: "Biografia e narrativa pessoal", elements: [{ key: "about_image", label: "Imagem" }, { key: "about_stats", label: "Estatísticas" }] },
                        { key: "portfolio", label: "Portfólio", desc: "Projetos em destaque", elements: [] },
                        { key: "testimonials", label: "Depoimentos", desc: "Feedbacks de clientes", elements: [] },
                        { key: "faq", label: "FAQ", desc: "Perguntas frequentes", elements: [{ key: "faq_items", label: "Lista de perguntas" }, { key: "faq_form", label: "Formulário de perguntas" }] },
                        { key: "contact", label: "Contato", desc: "Formulário e canais de contato", elements: [{ key: "contact_info", label: "Bloco de informações" }, { key: "contact_form", label: "Formulário de contato" }] },
                      ].map((s) => (
                        <div key={s.key} className="border border-fendi/20 hover:border-fendi/40 transition-colors">
                          <div className="flex items-center justify-between p-4">
                            <div>
                              <p className="text-sm font-medium text-softblack">{s.label}</p>
                              <p className="text-[11px] text-warmgray">{s.desc}</p>
                            </div>
                            <Switch
                              checked={sections?.[s.key] !== false}
                              onCheckedChange={async (checked) => {
                                try {
                                  await updateSetting(`section_${s.key}_visible`, checked.toString());
                                  setSections((prev) => ({ ...prev, [s.key]: checked }));
                                  toast.success(`${s.label} ${checked ? "ativada" : "desativada"}`);
                                } catch {
                                  toast.error("Erro ao salvar");
                                }
                              }}
                            />
                          </div>
                          {s.elements.length > 0 && (
                            <div className="border-t border-fendi/10 px-4 py-3 space-y-2 ml-6">
                              {s.elements.map((el) => (
                                <div key={el.key} className="flex items-center justify-between py-1">
                                  <p className="text-xs text-warmgray">{el.label}</p>
                                  <Switch
                                    checked={sections?.[el.key] !== false}
                                    onCheckedChange={async (checked) => {
                                      try {
                                        await updateSetting(`element_${el.key}_visible`, checked.toString());
                                        setSections((prev) => ({ ...prev, [el.key]: checked }));
                                        toast.success(`${el.label} ${checked ? "ativada" : "desativada"}`);
                                      } catch {
                                        toast.error("Erro ao salvar");
                                      }
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* SHARING / OPEN GRAPH SECTION */}
              <TabsContent value="sharing" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="space-y-6 max-w-4xl">
                  <div className="bg-white border border-fendi p-8 shadow-sm">
                    <p className="eyebrow mb-2 flex items-center gap-2 text-champagne"><Share2 size={16} /> Pré-visualização em Redes Sociais</p>
                    <p className="text-warmgray text-xs mb-8">Configure como o link aparece ao ser compartilhado no WhatsApp, Facebook, LinkedIn, etc.</p>

                    {/* Preview */}
                    <div className="bg-fendi/10 border border-fendi/20 rounded-lg p-6 mb-8 max-w-md">
                      <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                        {ogSettings.og_image && (
                          <img src={ogSettings.og_image} alt="Preview" className="w-full h-44 object-cover" />
                        )}
                        <div className="p-4">
                          <p className="text-[10px] uppercase tracking-wider text-warmgray">{new URL(ogSettings.og_url || "https://eprates.com").hostname}</p>
                          <p className="text-sm font-medium text-softblack mt-1 truncate">{ogSettings.og_title || "Hub Profissional"}</p>
                          <p className="text-xs text-warmgray mt-1 line-clamp-2">{ogSettings.og_description || ""}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-5">
                      <label className="block">
                        <span className="eyebrow text-[10px] text-warmgray">Título</span>
                        <input
                          className="editorial-field mt-2 !text-sm"
                          value={ogSettings.og_title}
                          onChange={(e) => setOgSettings({ ...ogSettings, og_title: e.target.value })}
                          placeholder="Hub Profissional — Emilly Prates"
                        />
                      </label>

                      <label className="block">
                        <span className="eyebrow text-[10px] text-warmgray">Descrição</span>
                        <textarea
                          className="editorial-field mt-2 !text-sm"
                          rows={2}
                          value={ogSettings.og_description}
                          onChange={(e) => setOgSettings({ ...ogSettings, og_description: e.target.value })}
                          placeholder="Consultoria financeira, Gestão de Patrimônio e Design"
                        />
                      </label>

                      <label className="block">
                        <span className="eyebrow text-[10px] text-warmgray">URL do site</span>
                        <input
                          className="editorial-field mt-2 !text-sm"
                          value={ogSettings.og_url}
                          onChange={(e) => setOgSettings({ ...ogSettings, og_url: e.target.value })}
                          placeholder="https://emillyprates.com"
                        />
                      </label>

                      <label className="block">
                        <span className="eyebrow text-[10px] text-warmgray">Imagem (1200×630 recomendado)</span>
                        <div className="flex gap-3 mt-2">
                          <input
                            className="editorial-field !text-sm flex-1"
                            value={ogSettings.og_image}
                            onChange={(e) => setOgSettings({ ...ogSettings, og_image: e.target.value })}
                            placeholder="https://..."
                          />
                          <label className="btn-primary !py-2 !px-4 cursor-pointer flex items-center gap-2 shrink-0">
                            <Upload size={14} /> Upload
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const url = await uploadSiteImage(file);
                                  setOgSettings({ ...ogSettings, og_image: url });
                                  toast.success("Imagem enviada");
                                } catch {
                                  toast.error("Erro ao enviar imagem");
                                }
                              }}
                            />
                          </label>
                        </div>
                      </label>
                    </div>

                    <div className="flex justify-end mt-8 pt-4 border-t border-fendi/10">
                      <button
                        onClick={async () => {
                          try {
                            await Promise.all([
                              updateSetting("og_title", ogSettings.og_title),
                              updateSetting("og_description", ogSettings.og_description),
                              updateSetting("og_image", ogSettings.og_image),
                              updateSetting("og_url", ogSettings.og_url),
                            ]);
                            toast.success("Configurações de compartilhamento salvas");
                          } catch {
                            toast.error("Erro ao salvar");
                          }
                        }}
                        disabled={saving}
                        className="btn-primary !px-8 !py-2 !text-[10px]"
                      >
                        <Save size={12} className="mr-2" /> Salvar
                      </button>
                    </div>
                  </div>
                </div>
              </TabsContent>

             {/* SERVICES SECTION */}
        <TabsContent value="services" className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-fendi p-4 md:p-6">
            <div>
              <p className="eyebrow">Estrutura de Serviços</p>
              <p className="text-warmgray text-xs mt-1 italic">Gerencie a ordem e o conteúdo das frentes de atuação.</p>
            </div>
            <button onClick={handleAddService} className="btn-primary flex items-center justify-center gap-2 !py-2 shadow-sm w-full sm:w-auto">
              <Plus size={16} /> Adicionar Serviço
            </button>
          </div>

          <div className="grid gap-6">
            {services.map((service, idx) => (
              <div key={service.id} className={`bg-white border ${service.published ? 'border-fendi' : 'border-dashed border-warmgray/60'} group overflow-hidden transition-all hover:shadow-md`}>
                <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 md:px-8 py-3 md:py-4 ${service.published ? 'bg-fendi/5' : 'bg-warmgray/5'} border-b border-fendi/30`}>
                  <div className="flex items-center gap-3">
                    <span className={`font-serif text-lg md:text-xl ${service.published ? 'text-champagne' : 'text-warmgray/60'}`}>{(idx + 1).toString().padStart(2, '0')}</span>
                    <div className="flex flex-col">
                      <h3 className="font-medium text-softblack uppercase tracking-[0.2em] text-[10px]">{service.title || "Novo Serviço"}</h3>
                      {!service.published && <span className="text-[8px] text-warmgray uppercase tracking-widest mt-0.5 flex items-center gap-1"><EyeOff size={8} /> Rascunho</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5 bg-white/50 px-2 md:px-3 py-1 border border-fendi/20 rounded-full">
                      <span className="text-[8px] md:text-[9px] uppercase tracking-wider text-warmgray hidden xs:inline">{service.published ? "Publicado" : "Privado"}</span>
                      <Switch 
                        checked={service.published} 
                        onCheckedChange={() => handleTogglePublish(service.id, service.published)} 
                        className="scale-75"
                      />
                    </div>
                    <div className="flex border border-fendi/40 rounded-sm overflow-hidden bg-white">
                      <button 
                        disabled={idx === 0}
                        onClick={() => handleMoveService(idx, -1)}
                        className="p-1.5 md:p-2 hover:bg-fendi/10 disabled:opacity-30 border-r border-fendi/40 transition-colors"
                      >
                        <ChevronUp size={12} strokeWidth={1.5} />
                      </button>
                      <button 
                        disabled={idx === services.length - 1}
                        onClick={() => handleMoveService(idx, 1)}
                        className="p-1.5 md:p-2 hover:bg-fendi/10 disabled:opacity-30 transition-colors"
                      >
                        <ChevronDown size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                    <button 
                      onClick={() => setServiceToDelete(service.id)}
                      className="p-1.5 md:p-2 text-destructive hover:bg-destructive/10 rounded-sm transition-colors"
                      title="Remover Serviço"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                
                <div className="p-8">
                  <div className="grid md:grid-cols-4 gap-8">
                    <div className="md:col-span-1">
                      <span className="eyebrow block mb-3 text-[10px]">Imagem de Detalhe</span>
                      <div className="relative group w-full aspect-[4/3] bg-fendi/10 overflow-hidden border border-fendi/30 rounded-sm">
                        {service.image_url ? (
                          <img src={service.image_url} alt="Service" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-warmgray"><Camera size={24} strokeWidth={1} /></div>
                        )}
                        <label className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white">
                          {service.image_url && (
                            <button 
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveImage("service", service.id); }}
                              className="absolute top-2 right-2 bg-white/10 hover:bg-destructive p-1.5 rounded-full transition-all"
                              title="Remover Imagem"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                          {uploading.services[service.id] ? <Loader2 className="animate-spin" /> : <Camera size={20} />}
                          <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, "service", service.id)} disabled={uploading.services[service.id]} />
                        </label>
                      </div>
                    </div>

                    <div className="md:col-span-3 space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <label className="block">
                          <span className="eyebrow text-[10px]">Título do Serviço</span>
                          <input className="editorial-field mt-2" value={service.title} 
                            onChange={e => {
                              const newS = services.map(s => s.id === service.id ? {...s, title: e.target.value} : s);
                              setServices(newS);
                            }} />
                        </label>
                        <label className="block">
                          <span className="eyebrow text-[10px]">Ícone (Lucide Name)</span>
                          <input className="editorial-field mt-2" value={service.icon_name} 
                            onChange={e => {
                              const newS = services.map(s => s.id === service.id ? {...s, icon_name: e.target.value} : s);
                              setServices(newS);
                            }} />
                        </label>
                      </div>
                      <label className="block">
                        <span className="eyebrow text-[10px]">Descrição Editorial</span>
                        <textarea className="editorial-field mt-2 text-sm" rows={2} value={service.description} 
                          onChange={e => {
                            const newS = services.map(s => s.id === service.id ? {...s, description: e.target.value} : s);
                            setServices(newS);
                          }} />
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 border-t border-fendi/20 pt-6">
                    <span className="eyebrow block mb-4 text-[10px]">Pontos de Destaque (Bullets)</span>
                    <div className="grid md:grid-cols-3 gap-4">
                      {service.bullets?.map((b, bIdx) => (
                        <div key={bIdx} className="flex gap-2">
                          <input className="editorial-field text-xs !py-2" value={b} 
                            onChange={e => {
                              const newBullets = [...service.bullets];
                              newBullets[bIdx] = e.target.value;
                              const newS = services.map(s => s.id === service.id ? {...s, bullets: newBullets} : s);
                              setServices(newS);
                            }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button 
                      onClick={() => handleSaveService(service.id, {
                        title: service.title, 
                        description: service.description,
                        image_url: service.image_url,
                        icon_name: service.icon_name,
                        bullets: service.bullets,
                        published: service.published
                      })} 
                      className="btn-primary !text-[10px] !py-2 !px-6"
                    >
                      <Save size={12} className="mr-2" /> Salvar Alterações
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

          </div>
        </div>
      </Tabs>

      {/* Deletion Confirmation Modal */}
      <AlertDialog open={!!serviceToDelete} onOpenChange={() => setServiceToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Remover Serviço?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação removerá permanentemente o serviço da sua lista e da página principal. Você não poderá recuperar estes dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Manter Serviço</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteService} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
              Confirmar Exclusão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={paragraphToDelete !== null} onOpenChange={() => setParagraphToDelete(null)}>
        <AlertDialogContent className="max-w-md border border-fendi/10 bg-offwhite">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Remover Parágrafo?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Este parágrafo será removido da seção Sobre na página principal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const paragraphs = [...(about.paragraphs || [about.description_1 || "", about.description_2 || ""].filter(Boolean))];
                paragraphs.splice(paragraphToDelete, 1);
                setAbout({ ...about, paragraphs });
                setParagraphToDelete(null);
              }}
              className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
